import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Eye, EyeOff } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button, Field, TextInput } from "@/components/primitives";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/auth")({
  validateSearch: (search: Record<string, unknown>): { mode?: "signup" | undefined; tier?: "gate" | "yard" } => ({
    mode: search['mode'] === "signup" ? "signup" : undefined,
    ...(search['tier'] === "gate" || search['tier'] === "yard" ? { tier: search['tier'] } : {}),
  }),
  head: () => ({
    meta: [
      { title: "Sign in — PortDwellr" },
      { name: "description", content: "Sign in to your PortDwellr yard: live gate manifest, sequencing, dock assignment and the timestamp record." },
      { property: "og:title", content: "Sign in — PortDwellr" },
      { property: "og:description", content: "Access your autonomous yard intelligence workspace — five agents, one closed loop." },
      { property: "og:url", content: "https://portdwellr.com/auth" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://portdwellr.com/og-image.png" },
      { property: "og:image:secure_url", content: "https://portdwellr.com/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "PortDwellr — autonomous yard intelligence" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Sign in — PortDwellr" },
      { name: "twitter:description", content: "Access your autonomous yard intelligence workspace." },
      { name: "twitter:image", content: "https://portdwellr.com/og-image.png" },
      { name: "twitter:image:alt", content: "PortDwellr — autonomous yard intelligence" },
      { name: "robots", content: "noindex" },

    ],
    links: [{ rel: "canonical", href: "https://portdwellr.com/auth" }],
  }),

  component: AuthPage,
});

const LOOP = [
  ["SENSE", "Trucks check in by QR or SMS the moment they hit the gate."],
  ["SEQUENCE", "The queue orders itself by arrival, appointment window and trailer type."],
  ["ASSIGN", "Dock doors and staging spots are allocated in real time."],
  ["RESOLVE", "Every touchpoint is timestamped — that record settles disputes."],
  ["LEARN", "Throughput is studied nightly and fed back into tomorrow's queue."],
] as const;

const SITE_KEY = import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined;

function AuthPage() {
  const { session, ready, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const { mode: initialMode, tier } = Route.useSearch();
  const [mode, setMode] = useState<"signin" | "signup">(initialMode === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Exactly one Turnstile widget on the page; its token serves whichever
  // action the operator submits (sign-in or sign-up).
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const widget = useRef<TurnstileInstance | null>(null);

  // A tier picked on the pricing page survives sign-up and onboarding.
  useEffect(() => {
    if (tier) sessionStorage.setItem("pd_tier", tier);
  }, [tier]);

  useEffect(() => {
    if (!ready || !session) return;
    if (!session.yardConfigured) {
      navigate({ to: "/onboarding", replace: true });
      return;
    }
    if (tier) navigate({ to: "/settings/billing", search: { tier }, replace: true });
    else navigate({ to: "/", replace: true });
  }, [ready, session, navigate, tier]);

  // Switching between sign-in and sign-up is a different form — force a fresh token.
  useEffect(() => {
    setCaptchaToken(null);
    setError(null);
    widget.current?.reset();
  }, [mode]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || !captchaToken || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "signup") {
        await signUp(email, password, captchaToken);
        navigate({ to: "/onboarding", replace: true });
      } else {
        await signIn(email, password, captchaToken);
        navigate({ to: "/", replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed. Try again.");
      setCaptchaToken(null);
      widget.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  const notice = (text: string) => (
    <p className="border border-signal-deep bg-paper-raised px-4 py-3 font-mono text-[12px] text-signal-deep">
      {text}
    </p>
  );


  return (
    <div className="grid min-h-screen grid-cols-1 bg-paper lg:grid-cols-[1fr_1.05fr]">
      <div className="flex flex-col justify-center border-b border-line px-6 py-14 md:px-14 lg:border-b-0 lg:border-r">
        <Logo />
        <span className="eyebrow mt-10">{mode === "signup" ? "Create account" : "Operator access"}</span>
        <h1 className="mt-4 max-w-[16ch] font-display text-[34px] font-semibold leading-[1.08] md:text-[42px]">
          {mode === "signup" ? "Put the loop on your gate." : "Sign in to your yard."}
        </h1>

        <form onSubmit={submit} className="mt-9 max-w-[420px] space-y-5">
          <Field label="Work email">
            <TextInput
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="dispatch@yard.com"
            />
          </Field>
          <Field label="Password">
            <div className="relative">
              <TextInput
                type={showPassword ? "text" : "password"}
                required
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center border-0 bg-transparent text-ink-soft hover:text-steel focus:text-steel focus:outline-none focus:ring-2 focus:ring-steel/30"
              >
                {showPassword ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
              </button>
            </div>
          </Field>

          {SITE_KEY ? (
            <Turnstile
              ref={widget}
              siteKey={SITE_KEY}
              options={{ theme: "light" }}
              onSuccess={(token) => {
                setCaptchaToken(token);
                setError(null);
              }}
              onError={() => {
                setCaptchaToken(null);
                setError("Bot check failed. Complete the challenge again before continuing.");
              }}
              onExpire={() => {
                setCaptchaToken(null);
                setError("Bot check expired. Complete the challenge again.");
                widget.current?.reset();
              }}
            />
          ) : (
            notice("Turnstile site key is not configured — set VITE_TURNSTILE_SITE_KEY.")
          )}

          <Button type="submit" disabled={!captchaToken || busy}>
            {busy ? "Working…" : mode === "signup" ? "Create account" : "Sign in"}
          </Button>

          {error ? notice(error) : null}
        </form>


        <p className="max-w-[420px] pt-6 text-[14px] text-ink-soft">
          {mode === "signup" ? "Already running a yard here?" : "First time here?"}{" "}
          <button
            type="button"
            className="font-semibold text-steel underline decoration-signal decoration-2 underline-offset-4"
            onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
          >
            {mode === "signup" ? "Sign in" : "Create an account"}
          </button>
        </p>
      </div>


      <div className="flex items-center bg-steel px-6 py-14 text-paper md:px-14">
        <div>
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-signal">The five-agent loop</span>
          <ol className="mt-8 border-t border-paper/20">
            {LOOP.map(([stage, copy], i) => (
              <li key={stage} className="grid grid-cols-[64px_1fr] gap-4 border-b border-paper/20 py-5">
                <span className="font-mono text-[12px] text-signal">0{i + 1}</span>
                <div>
                  <div className="font-display text-[17px] font-semibold">{stage}</div>
                  <p className="mt-1 max-w-[46ch] text-[14px] text-paper/70">{copy}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.08em] text-paper/60">
            LEARN feeds back into SENSE
          </p>
        </div>
      </div>
    </div>
  );
}
